import { useEffect, useState } from "react";
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ResponsiveDialog } from "@/components/ui/ResponsiveDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus,
  Loader2,
  CheckCircle2,
  MapPin,
  Crosshair,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { api, resolvePlace } from "@/lib/api";
import { CUISINES } from "@/lib/cuisines";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";

export default function AddRestaurantDialog({ open, onOpenChange, initialCenter, userLocation }) {
  const [form, setForm] = useState(blank(initialCenter));
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [pickerInput, setPickerInput] = useState("");
  const [picked, setPicked] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [manualEdit, setManualEdit] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(blank(initialCenter));
      setDone(false);
      setPickerInput("");
      setPicked(false);
      setManualEdit(false);
    }
  }, [open, initialCenter]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        update("lat", pos.coords.latitude);
        update("lng", pos.coords.longitude);
        toast.success("Location pinned");
      },
      () => toast.error("Couldn't get your location")
    );
  };

  const handlePick = async (s) => {
    if (!s?.place_id) return;
    setResolving(true);
    try {
      const place = await resolvePlace(s.place_id);
      setForm((f) => ({
        ...f,
        name: place.name || f.name,
        address: place.address || f.address,
        lat: place.lat,
        lng: place.lng,
        phone: place.phone || f.phone,
        website: place.website || f.website,
      }));
      setPickerInput(place.name || s.description);
      setPicked(true);
      toast.success("Location pinned from Google Maps");
    } catch (err) {
      toast.error("Couldn't load that place", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setResolving(false);
    }
  };

  const clearPick = () => {
    setPicked(false);
    setPickerInput("");
    setManualEdit(false);
    setForm((f) => ({
      ...f,
      name: "",
      address: "",
      lat: "",
      lng: "",
      phone: "",
      website: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        lat: Number(form.lat),
        lng: Number(form.lng),
        is_owner: !!form.is_owner,
        halal_has_alcohol:
          form.halal_status === "certified" ? false : !!form.halal_has_alcohol,
      };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
          delete payload[k];
        }
      });
      await api.post("/submissions", payload);
      setDone(true);
      toast.success("Submitted for review");
    } catch (err) {
      toast.error("Submission failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      testId="add-restaurant-dialog"
      dialogClassName="sm:max-w-[520px] p-0 overflow-hidden bg-white border-[#E5E1D8] rounded-3xl"
    >
      <div className="flex flex-col">
        <div className="px-6 pt-6 pb-4 bg-gradient-to-br from-[#FDFBF7] to-[#F2EDE3] border-b border-[#E5E1D8] shrink-0">
          <DialogHeader className="space-y-1.5 text-left">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#2A3B2C] flex items-center justify-center">
                <Plus className="w-4 h-4 text-white" />
              </div>
              <DialogTitle className="font-display text-xl font-medium text-[#2A3B2C]">
                Add a halal spot
              </DialogTitle>
            </div>
            <DialogDescription className="text-sm text-[#647466]">
              Search Google Maps to pin the exact location — name and address are
              filled automatically. Submissions go through a quick admin review.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div
          className="p-6 overflow-y-auto brand-scroll max-h-[calc(85dvh-110px)] sm:max-h-[65vh]"
          style={{ WebkitOverflowScrolling: "touch" }}
          onFocus={(e) => {
            const t = e.target;
            if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) {
              setTimeout(() => t.scrollIntoView({ block: "center", behavior: "smooth" }), 250);
            }
          }}
        >
          {done ? (
            <div className="text-center py-6" data-testid="submit-success">
              <CheckCircle2 className="w-12 h-12 mx-auto text-[#D95D39]" />
              <h4 className="mt-3 font-display text-lg font-medium text-[#2A3B2C]">
                Thanks — pending review
              </h4>
              <p className="mt-1 text-sm text-[#647466]">
                We'll publish this once an admin approves it (usually within 48
                hours).
              </p>
              <button
                onClick={() => onOpenChange(false)}
                className="mt-5 inline-flex items-center justify-center gap-2 h-10 px-5 rounded-full bg-[#2A3B2C] text-white text-sm font-medium"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="submit-form">
              {/* Google Places picker */}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#647466]">
                  Find on Google Maps <span className="text-[#D95D39] ml-1">*</span>
                </Label>
                {picked ? (
                  <div
                    data-testid="picked-place-summary"
                    className="rounded-xl border border-[#D95D39] bg-[#FFF3EE] p-3 flex items-start gap-3"
                  >
                    <MapPin className="w-4 h-4 mt-0.5 text-[#D95D39] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#2A3B2C] truncate text-sm">
                        {form.name}
                      </div>
                      <div className="text-xs text-[#647466] truncate">
                        {form.address}
                      </div>
                      <div className="text-[10px] text-[#8A9A8B] mt-1 font-mono">
                        {Number(form.lat).toFixed(5)}, {Number(form.lng).toFixed(5)}
                      </div>
                    </div>
                    <button
                      type="button"
                      data-testid="picked-place-change"
                      onClick={clearPick}
                      className="text-xs text-[#D95D39] hover:text-[#C54E2C] font-medium"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <>
                    <PlaceAutocomplete
                      value={pickerInput}
                      onChange={setPickerInput}
                      onPick={handlePick}
                      userLocation={userLocation || initialCenter}
                      restaurantsOnly={true}
                      placeholder="Start typing a restaurant name…"
                      testId="sub-place-picker"
                    />
                    {resolving && (
                      <div className="text-xs text-[#647466] flex items-center gap-1.5 pt-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Pinning location…
                      </div>
                    )}
                    {!manualEdit && (
                      <button
                        type="button"
                        data-testid="sub-manual-toggle"
                        onClick={() => setManualEdit(true)}
                        className="text-xs text-[#647466] hover:text-[#D95D39] inline-flex items-center gap-1 pt-1"
                      >
                        <Pencil className="w-3 h-3" />
                        Can't find it? Enter details manually
                      </button>
                    )}
                  </>
                )}
              </div>

              {(picked || manualEdit) && (
                <>
                  {manualEdit && !picked && (
                    <>
                      <Field
                        label="Restaurant name"
                        value={form.name}
                        onChange={(v) => update("name", v)}
                        placeholder="Bismillah Biryani House"
                        required
                        testId="sub-name"
                      />
                      <Field
                        label="Full address"
                        value={form.address}
                        onChange={(v) => update("address", v)}
                        placeholder="123 Lygon St, Carlton VIC 3053"
                        required
                        testId="sub-address"
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <Field
                          label="Latitude"
                          type="number"
                          step="any"
                          value={form.lat}
                          onChange={(v) => update("lat", v)}
                          required
                          testId="sub-lat"
                        />
                        <Field
                          label="Longitude"
                          type="number"
                          step="any"
                          value={form.lng}
                          onChange={(v) => update("lng", v)}
                          required
                          testId="sub-lng"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={useMyLocation}
                        data-testid="sub-use-my-location"
                        className="-mt-2 text-xs text-[#D95D39] hover:text-[#C54E2C] inline-flex items-center gap-1"
                      >
                        <Crosshair className="w-3 h-3" />
                        Use my current location
                      </button>
                    </>
                  )}

                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#647466]">
                      Cuisine
                    </Label>
                    <select
                      data-testid="sub-cuisine"
                      value={form.cuisine}
                      onChange={(e) => update("cuisine", e.target.value)}
                      className="w-full h-10 rounded-xl border border-[#E5E1D8] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D95D39]"
                    >
                      <option value="">— select —</option>
                      {CUISINES.filter((c) => c.id !== "all").map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Field
                      label="Phone (optional)"
                      value={form.phone}
                      onChange={(v) => update("phone", v)}
                      testId="sub-phone"
                    />
                    <Field
                      label="Website (optional)"
                      value={form.website}
                      onChange={(v) => update("website", v)}
                      placeholder="https://..."
                      testId="sub-website"
                    />
                  </div>

                  <div className="rounded-2xl border border-[#2A3B2C]/15 bg-[#2A3B2C]/[0.03] p-4 space-y-3">
                    <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#2A3B2C]">
                      Halal info
                    </div>
                    <select
                      data-testid="sub-halal-status"
                      value={form.halal_status}
                      onChange={(e) => update("halal_status", e.target.value)}
                      className="w-full h-10 rounded-xl border border-[#E5E1D8] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D95D39]"
                    >
                      <option value="">— pick one —</option>
                      <option value="certified">Officially halal-certified</option>
                      <option value="muslim_owned">Muslim-owned · fully halal</option>
                      <option value="self_declared">Self-declared halal</option>
                    </select>
                    {form.halal_status === "certified" && (
                      <Field
                        label="Certifier (optional)"
                        value={form.halal_certification_body}
                        onChange={(v) => update("halal_certification_body", v)}
                        placeholder="JAKIM, HFA, ISWA..."
                        testId="sub-halal-cert"
                      />
                    )}
                    <label className="flex items-center gap-2 text-xs text-[#647466] cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="sub-is-owner"
                        checked={!!form.is_owner}
                        onChange={(e) => update("is_owner", e.target.checked)}
                        className="w-4 h-4 rounded border-[#E5E1D8] text-[#D95D39] focus:ring-[#D95D39]"
                      />
                      I am the owner / manager
                    </label>
                  </div>

                  <Field
                    label="Your email (optional)"
                    type="email"
                    value={form.submitter_email}
                    onChange={(v) => update("submitter_email", v)}
                    placeholder="So we can follow up"
                    testId="sub-email"
                  />

                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#647466]">
                      Notes (optional)
                    </Label>
                    <Textarea
                      data-testid="sub-notes"
                      value={form.notes}
                      onChange={(e) => update("notes", e.target.value)}
                      placeholder="Anything special? Best dish, opening hours, etc."
                      rows={2}
                      className="border-[#E5E1D8] focus-visible:ring-[#D95D39] rounded-xl resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !form.name || !form.address || !form.lat || !form.lng}
                    data-testid="submit-restaurant-btn"
                    className="w-full h-11 rounded-full bg-[#D95D39] hover:bg-[#C54E2C] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium inline-flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        Submit for review
                      </>
                    )}
                  </button>
                </>
              )}
            </form>
          )}
        </div>
      </div>
    </ResponsiveDialog>
  );
}

function blank(c) {
  return {
    name: "",
    address: "",
    lat: c?.lat ?? "",
    lng: c?.lng ?? "",
    cuisine: "",
    phone: "",
    website: "",
    halal_status: "",
    halal_certification_body: "",
    halal_has_alcohol: false,
    submitter_email: "",
    submitter_name: "",
    is_owner: false,
    notes: "",
  };
}

function Field({ label, value, onChange, placeholder, type = "text", step, required, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#647466]">
        {label}
        {required && <span className="text-[#D95D39] ml-1">*</span>}
      </Label>
      <Input
        data-testid={testId}
        type={type}
        step={step}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="border-[#E5E1D8] focus-visible:ring-[#D95D39] rounded-xl h-10"
      />
    </div>
  );
}
